import {Navigate, Outlet, Route, Routes} from "react-router-dom"
import {DocumentEditModalRoute} from "@/components/documents/DocumentEditModalRoute"
import {DocumentModalRoute} from "@/components/documents/DocumentModalRoute"
import {RequireAuth} from "@/components/auth/RequireAuth"
import {RequireGuest} from "@/components/auth/RequireGuest"
import {AppShell} from "@/layout/AppShell"
import {ConnectSubject} from "@/pages/ConnectSubject"
import {Documents} from "@/pages/Documents"
import {DocumentsSearch} from "@/pages/DocumentsSearch"
import {Login} from "@/pages/Login"
import {PrintProfiles} from "@/pages/PrintProfiles"
import {SubjectProfile} from "@/pages/SubjectProfile"
import {SubjectResponsible} from "@/pages/SubjectResponsible"
import {SubjectRoles} from "@/pages/SubjectRoles"
import {SubjectUsers} from "@/pages/SubjectUsers"
import {Warehouse} from "@/pages/Warehouse"
import {WarehouseActs} from "@/pages/WarehouseActs"
import {useSession} from "@/store/session"

/**
 * `/` (the workspace/dashboard) doesn't exist yet in this package — it's
 * login.md's own documented post-login destination (L2/L3's "land in the
 * workspace"), not something this login-page scaffolding task builds. This
 * stand-in exists only so that navigation lands somewhere inspectable
 * instead of the catch-all route bouncing it straight back to /login.
 */
function WorkspacePlaceholder() {
    const {user, subject, connectedSubject} = useSession()

    return (
        <pre className="text-sm">
      {JSON.stringify({user, subject, connectedSubject}, null, 2)}
    </pre>
    )
}

/** Wraps `app-shell.md`'s sidebar/header frame around its nested routes. */
function AppShellLayout() {
    return (
        <AppShell>
            <Outlet/>
        </AppShell>
    )
}

export function AppRoutes() {
    return (
        <Routes>
            <Route element={<RequireGuest/>}>
                <Route element={<Login/>} path="/login"/>
            </Route>
            <Route element={<RequireAuth/>}>
                {/* Same standalone-frame treatment as /login (see LoginFrame) —
            a user mid-onboarding with no subject connected yet shouldn't
            see the workspace shell around it. */}
                <Route element={<ConnectSubject/>} path="/connect-subject"/>
                {/* subject-profile.md's conditional-rendering rules: a hand-typed
            /subject/* sub-path with no route of its own below renders
            nothing at all (not the page, not AppShell chrome) rather than
            falling through to the app-wide "*" redirect-to-login. Declared
            here rather than inside AppShellLayout below so it stays
            chrome-free; React Router ranks the static routes nested under
            AppShellLayout above this wildcard regardless of which branch
            declares them. */}
                <Route element={null} path="/subject/*"/>
                <Route element={<AppShellLayout/>}>
                    <Route element={<WorkspacePlaceholder/>} path="/"/>
                    {/* documents.md: both /documents and /documents/search keep their
              own list/search page mounted underneath the document modal —
              :id (view) and edit/:id (edit) are nested routes, not separate
              pages, so the modal opens above the list without unmounting
              it. Edit has its own dedicated route/component
              (`DocumentEditModalRoute`) because it fetches the fuller
              `OtkDocumentDetail` by id instead of reading the list's own
              already-fetched summary rows. */}
                    <Route element={<Documents/>} path="/documents">
                        <Route element={<DocumentEditModalRoute/>} path="edit/:id"/>
                        <Route element={<DocumentModalRoute base="/documents"/>} path=":id"/>
                    </Route>
                    <Route element={<DocumentsSearch/>} path="/documents/search">
                        <Route element={<DocumentModalRoute base="/documents/search"/>} path=":id"/>
                    </Route>
                    {/* print-profiles.md: subject-level, not пункт-scoped — same
              access rule as /notifications, no permission gate. */}
                    <Route element={<PrintProfiles/>} path="/print-profiles"/>
                    {/* warehouse.md: bare /warehouse redirects to /warehouse/blanks,
              same `<Navigate replace>` pattern as bare /subject below.
              /warehouse/acts is warehouse-acts.md's read-only archive. */}
                    <Route element={<Navigate replace to="/warehouse/blanks"/>} path="/warehouse"/>
                    <Route element={<Warehouse/>} path="/warehouse/blanks"/>
                    <Route element={<WarehouseActs/>} path="/warehouse/acts"/>
                    {/* subject-profile.md's conditional-rendering rules: bare
              /subject and the merged-away /subject/punkty both redirect to
              /subject/profile. */}
                    <Route element={<Navigate replace to="/subject/profile"/>} path="/subject"/>
                    <Route element={<SubjectProfile/>} path="/subject/profile"/>
                    <Route element={<Navigate replace to="/subject/profile"/>} path="/subject/punkty"/>
                    <Route element={<SubjectUsers/>} path="/subject/users"/>
                    <Route element={<SubjectRoles/>} path="/subject/roles"/>
                    <Route element={<SubjectResponsible/>} path="/subject/responsible"/>
                </Route>
            </Route>
            <Route element={<Navigate replace to="/login"/>} path="*"/>
        </Routes>
    )
}
